import { useState } from 'react';
import { MapPin, Shield, Trophy, Star, Zap, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

const tabs = [
  {
    id: 'path-to-pro',
    title: 'Path to Pro',
    subtitle: 'Your Journey to Greatness',
    description:
      'Every drill completed and Soccer IQ scenario conquered earns XP that fuels your rise through five prestige zones — from The Grassroots to The Legend Void. Unlock badges, stadiums, cleats, jerseys, trophies, and more as you climb.',
    icon: MapPin,
    accentColor: 'amber',
    video: '/videos/road_map.mp4',
    showcase: [
      { label: 'The Grassroots', rarity: 'common', icon: Star },
      { label: 'The Academy', rarity: 'rare', icon: Zap },
      { label: 'Command Center', rarity: 'epic', icon: Shield },
      { label: 'Elite Arena', rarity: 'legendary', icon: Trophy },
      { label: 'Legend Void', rarity: 'legendary', icon: Star },
    ],
  },
  {
    id: 'equipment',
    title: 'Equipment & Locker Room',
    subtitle: 'Make It Yours',
    description:
      'Equip unlocked gear, swap stadium backgrounds, change kits, select cleats, and personalize your entire experience. Your locker room is your identity — make it legendary.',
    icon: Shield,
    accentColor: 'emerald',
    video: '/videos/Locker_room.mp4',
    showcase: [
      { label: 'Stadiums', rarity: 'epic', icon: MapPin },
      { label: 'Cleats', rarity: 'rare', icon: Zap },
      { label: 'Kits', rarity: 'epic', icon: Shield },
      { label: 'Balls', rarity: 'legendary', icon: Star },
      { label: 'Trophies', rarity: 'legendary', icon: Trophy },
    ],
  },
];

const colorMap: Record<string, { tab: string; activeTab: string; ring: string; iconBg: string; iconText: string }> = {
  amber: {
    tab: 'hover:text-amber-600',
    activeTab: 'text-amber-600 border-amber-500',
    ring: 'ring-amber-500/30',
    iconBg: 'bg-amber-500/10',
    iconText: 'text-amber-600',
  },
  emerald: {
    tab: 'hover:text-emerald-600',
    activeTab: 'text-emerald-600 border-emerald-500',
    ring: 'ring-emerald-500/30',
    iconBg: 'bg-emerald-500/10',
    iconText: 'text-emerald-600',
  },
};

const rarityGradient: Record<string, string> = {
  common: 'from-zinc-700 to-zinc-800',
  rare: 'from-blue-600 to-blue-800',
  epic: 'from-purple-600 to-indigo-800',
  legendary: 'from-amber-500 to-orange-700',
};

const rarityGlow: Record<string, string> = {
  common: 'shadow-zinc-500/20',
  rare: 'shadow-blue-500/30',
  epic: 'shadow-purple-500/30',
  legendary: 'shadow-amber-500/40',
};

export function GamificationSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = tabs[activeIndex];
  const colors = colorMap[active.accentColor];
  const Icon = active.icon;

  return (
    <section className="py-10 md:py-16 bg-background">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6 md:mb-10">
          <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium mb-3">
            For Players
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
            Level Up Every Time You Train
          </h2>
          <p className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto">
            FutWork turns every session into progress — earn XP, unlock gear, and build your legacy.
          </p>
        </div>

        {/* Tab Bar */}
        <div className="flex justify-center border-b border-border mb-0">
          {tabs.map((tab, i) => {
            const c = colorMap[tab.accentColor];
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveIndex(i)}
                className={cn(
                  'flex items-center gap-1.5 px-4 md:px-6 py-3 text-sm font-medium border-b-2 transition-all -mb-px',
                  i === activeIndex
                    ? c.activeTab
                    : 'border-transparent text-muted-foreground ' + c.tab
                )}
              >
                <TabIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.title}</span>
                <span className="sm:hidden">{tab.title.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Active Content — side-by-side on desktop */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center pt-6 md:pt-10">
          {/* Left: Text */}
          <div key={active.id} className="animate-fade-in">
            <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center mb-4', colors.iconBg)}>
              <Icon className={cn('h-5 w-5', colors.iconText)} />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-1">
              {active.title}
            </h3>
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {active.subtitle}
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              {active.description}
            </p>
          </div>

          {/* Right: Showcase Grid */}
          <div key={active.id + '-visual'} className="animate-fade-in">
            <div className={cn('relative rounded-2xl overflow-hidden shadow-xl ring-1 bg-card/50 backdrop-blur-sm', colors.ring)}>
              <video
                src={active.video}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
